'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { z } from 'zod/v4'

import type { RouterOutputs } from '@package/api'
import type { SelectOrganization } from '@package/drizzle/schema'
import { Button, NumberFlow, Skeleton, toast } from '@ui/web/components'
import { WhatsappIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import { WHATSAPP_LINK } from '@helper/utils/core'
import { buildUrl, formatCurrency } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { SeatAssignmentValue } from '../../../lib'
import { useSharedTRPC } from '../../../context'
import {
  AssignSeatDialog,
  PriceCardGroup,
  resolveDefaultSeatAssignment,
  truncateDefaultSeatAssignment,
  VoucherList,
} from '../../../lib'
import { AnimatedCurrency } from './animated-currency'
import { CheckoutCard, CheckoutReveal } from './checkout-card'
import { SAVINGS_EXPLANATION, SavingsLine } from './savings-line'
import {
  calculateAmountPaid,
  calculateSavings,
  calculateTierSavings,
  calculateTotalDiscount,
  calculateTotalPriceWithoutVoucher,
  calculateVoucherDiscount,
  FAMILY_MINIMUM_SEATS,
  longerTermUpsell,
  nextTierUpsell,
  pricesForSeats,
  repointPriceToTier,
  SCHOOL_MINIMUM_SEATS,
} from './util'

type PriceItem = RouterOutputs['price']['getActivePrices'][number]
type VoucherItem = RouterOutputs['price']['getActiveVouchers'][number]

const checkoutSchema = z.object({
  selectedPriceId: z.uuid(),
  // The field is `null` until the default settles (see the effect in `NewForm`); submitting before
  // then is already blocked at the button, and rejected here as a second line.
  assignment: z.object({
    childIds: z.array(z.string()),
    emptySeats: z.number().int().min(0),
  }),
  selectedVoucherId: z.uuid().nullable(),
})

interface NewFormProps {
  organizationData: Pick<SelectOrganization, 'id' | 'name'>
  getAllPrice: RouterOutputs['price']['getActivePrices']
  initialCount: number | null
  onNavigate: (url: string) => void
  type: 'web' | 'school'
}

/**
 * Whether `voucher` still applies once `priceId` / `seats` change.
 *
 * Two independent conditions, unchanged from the previous `useEffect`: the voucher must still be
 * scoped to the chosen price (or scoped to none), and the subtotal it applies to must still clear
 * its own minimum transaction.
 */
function isVoucherStillValid(voucher: VoucherItem, priceId: string, price: PriceItem, seats: number): boolean {
  const appliesToPrice =
    voucher.voucherToPrices.length === 0 || voucher.voucherToPrices.some((vtp) => vtp.price.id === priceId)

  if (!appliesToPrice) return false

  if (voucher.minimumTransaction) {
    const amountBeforeVoucher = price.price * seats * price.duration
    if (amountBeforeVoucher < voucher.minimumTransaction) return false
  }

  return true
}

function countSeats(assignment: SeatAssignmentValue): number {
  return assignment.childIds.length + assignment.emptySeats
}

/**
 * Narrows `listChildren`'s seat-flavour fields to the shape `getSeatLockReason` requires.
 *
 * Mirrors `AssignSeatDialog`'s own `toChild`: `includeSeatFlavour` is a runtime boolean the
 * router types the three flavour fields against conditionally, so they come back optional even
 * though this page always requests them and they are always present at runtime.
 */
function toFlavouredChild(child: {
  id: string
  heldSeatId: string | null
  heldSeatSourceType?: string | null
  heldSeatOrganizationId?: string | null
  heldSeatIsCommercial?: boolean
  premiumId: string | null
  familyPremiumId: string | null
}) {
  return {
    id: child.id,
    heldSeatId: child.heldSeatId,
    heldSeatSourceType: child.heldSeatSourceType ?? null,
    heldSeatOrganizationId: child.heldSeatOrganizationId ?? null,
    heldSeatIsCommercial: child.heldSeatIsCommercial ?? false,
    premiumId: child.premiumId,
    familyPremiumId: child.familyPremiumId,
  }
}

export function NewForm({ organizationData, getAllPrice, initialCount, onNavigate, type }: NewFormProps) {
  const trpc = useSharedTRPC()

  const minSeats = type === 'school' ? SCHOOL_MINIMUM_SEATS : FAMILY_MINIMUM_SEATS

  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  // Settled by whichever path produces a real assignment first: the default effect below (the
  // ladder, or the deep link's truncation), or the buyer confirming the dialog themselves. Once
  // true, the effect never overwrites a choice the buyer already made (or is mid-way through
  // making) — a roster refetch after a manual pick must not recompute the default over it.
  const assignmentSettledRef = useRef(false)

  const checkoutMutation = useMutation(
    trpc.invoice.createInvoice.mutationOptions({
      onSuccess: (data) => {
        if (data.xenditInvoice) {
          onNavigate(data.xenditInvoice.invoiceUrl)
        } else {
          const successUrl = buildUrl(type, '/checkout/success', {
            invoice_id: data.invoice.id,
            type: 'NEW',
          }).href
          onNavigate(successUrl)
        }
      },
    }),
  )

  /**
   * The rate card the buyer can actually reach, at the seat count they arrived with.
   *
   * Volume pricing means `getActivePrices` returns every tier, not just theirs — the stepper moves
   * the tier, and refetching per press would be a request per seat. Offering the whole card would
   * let a three-seat buyer pick the 50+ rate; the server re-resolves the tier anyway, so the form
   * would simply be quoting a price it then fails to charge.
   */
  const initialTierPrices = pricesForSeats(getAllPrice, initialCount ?? minSeats)

  const defaultPrice = initialTierPrices[initialTierPrices.length - 1]

  const { data: vouchersData } = useQuery(trpc.price.getActiveVouchers.queryOptions())

  const form = useForm({
    defaultValues: {
      selectedPriceId: defaultPrice?.id ?? '',
      // `null`, not a placeholder count: a non-null value here is what the dialog restores verbatim
      // on open, so seeding one would silence the default ladder for the whole session and the
      // card would read "Total kursi 1" to a school with forty eligible students. It stays null
      // only until the effect below settles it — from the roster, or from the deep link.
      assignment: null as SeatAssignmentValue | null,
      selectedVoucherId: null as string | null,
    },
    validators: { onSubmit: checkoutSchema },
    onSubmit: ({ value }) => {
      if (!selectedPrice || !data || !value.assignment || seats === null) return

      const amountPaid = calculateAmountPaid(data)

      checkoutMutation.mutate({
        organizationId: organizationData.id,
        type: 'NEW',
        seats,
        totalPrice: calculateTotalPriceWithoutVoucher({ type: 'new', data }),
        amountPaid,
        priceId: selectedPrice.id,
        voucherId: value.selectedVoucherId ?? undefined,
        totalDiscount: calculateVoucherDiscount(data),
        withXendit: amountPaid > 0,
        assignToChildIds: value.assignment.childIds,
      })
    },
  })

  const selectedPriceId = useStore(form.store, (s) => s.values.selectedPriceId)
  const assignment = useStore(form.store, (s) => s.values.assignment)
  const selectedVoucherId = useStore(form.store, (s) => s.values.selectedVoucherId)

  // The roster and pools the default ladder reads. Fetched from first render, not only for a deep
  // link — that is what puts the resolved count on the card before the dialog is ever opened — and
  // switched off once the assignment settles. `AssignSeatDialog` runs the same two queries under
  // the same keys, so opening it later costs no second round trip.
  const isAssignmentUnsettled = assignment === null

  const { data: childrenData, isError: isErrorChildren } = useQuery({
    ...trpc.myOrganization.listChildren.queryOptions({
      pageSize: 'all',
      includeSchoolClass: true,
      includeAllSchoolClasses: true,
      includeSeatFlavour: true,
    }),
    enabled: isAssignmentUnsettled,
  })

  const { data: poolsData, isError: isErrorPools } = useQuery({
    ...trpc.myOrganization.getSeatPools.queryOptions(),
    // A deep link fixes the total itself; only the bare path needs the trial pool's size.
    enabled: isAssignmentUnsettled && initialCount === null,
  })

  const trialPool = useMemo(
    () => (poolsData ?? []).find((pool) => pool.sourceType === 'free_trial') ?? null,
    [poolsData],
  )

  useEffect(() => {
    if (assignmentSettledRef.current) return

    let next: SeatAssignmentValue | null = null

    if (isErrorChildren) {
      // The roster could not be read, so there is nothing to run the ladder on. Settle on the bare
      // count anyway — the deep link's total, else the minimum — rather than leave Konfirmasi
      // disabled for as long as the request keeps failing. The dialog still offers "Coba lagi".
      next = { childIds: [], emptySeats: initialCount ?? minSeats }
    } else if (childrenData) {
      const children = childrenData.data.map(toFlavouredChild)

      if (initialCount !== null) {
        // Deep link: the default offerable selection truncated to the linked total, remainder as
        // empty seats, so the page shows exactly `initialCount` — never more (see ruling #1).
        next = truncateDefaultSeatAssignment({ children, organizationId: organizationData.id, total: initialCount })
      } else if (poolsData || isErrorPools) {
        // Bare path: the dialog's full default ladder — every eligible student, else the trial
        // pool mirrored, else the flat fallback. A failed pool read only loses the middle rung.
        next = resolveDefaultSeatAssignment({ children, organizationId: organizationData.id, trialPool })
      }
    }

    if (!next) return

    form.setFieldValue('assignment', next)
    assignmentSettledRef.current = true
  }, [
    childrenData,
    isErrorChildren,
    poolsData,
    isErrorPools,
    trialPool,
    initialCount,
    minSeats,
    form,
    organizationData.id,
  ])

  useEffect(() => {
    if (checkoutMutation.error) {
      toast.error('Ada error yang terjadi saat ingin mencoba bayar')
    }
  }, [checkoutMutation.error])

  const selectedVoucher = vouchersData?.find((voucher) => voucher.id === selectedVoucherId) ?? null
  // `null` while the assignment is unsettled. Every money figure below prices `seats ?? 0` and is
  // drawn as a skeleton in that window, so the card never flashes a count that is about to change.
  const seats = assignment === null ? null : countSeats(assignment)

  // Re-narrowed on every seat change, which is what moves the buyer between tiers.
  const tierPrices = pricesForSeats(getAllPrice, seats ?? initialCount ?? minSeats)

  const namedPrice = getAllPrice.find((price) => price.id === selectedPriceId)
  // Keeps the buyer's TERM and swaps the tier when the stepper crosses a boundary.
  const selectedPrice = namedPrice ? repointPriceToTier(getAllPrice, namedPrice, seats ?? minSeats) : undefined

  // Null while the assignment is unsettled, while no row is selected, and once the buyer already
  // sits in the cheapest tier their term offers.
  const upsell = seats === null || !selectedPrice ? null : nextTierUpsell(getAllPrice, selectedPrice, seats)

  // The dropdown's own nudge: same tier, longer term, cheaper every month.
  const termUpsell = seats === null || !selectedPrice ? null : longerTermUpsell(getAllPrice, selectedPrice, seats)

  // What the volume tier is worth, beside what the longer term is worth. `calculateSavings` already
  // covers the term half — it compares this row against the dearest term in the same tier.
  const tierSavings = seats === null || !selectedPrice ? 0 : calculateTierSavings(getAllPrice, selectedPrice, seats)

  /**
   * Take the nudge: buy the seats that reach the next tier, as EMPTY seats.
   *
   * Empty rather than assigned, because there is nobody left to assign — the default already seats
   * every student this purchase may seat, so the gap to the boundary is seats the school is parking
   * for later. The buyer can still hand them out from the dialog.
   *
   * Settles the assignment first: this is a deliberate choice, and the roster effect must not
   * recompute its default over it when `listChildren` next resolves.
   */
  const handleTakeUpsell = () => {
    if (!upsell || assignment === null) return

    const next: SeatAssignmentValue = { ...assignment, emptySeats: assignment.emptySeats + upsell.seatsNeeded }

    assignmentSettledRef.current = true
    form.setFieldValue('assignment', next)
  }

  /**
   * Write the re-pointed row back into the field.
   *
   * The field holds the row the buyer NAMED, while `selectedPrice` is the row their seat count
   * actually lands on, and the two diverge the moment the stepper crosses a tier boundary. The
   * dropdown is bound to the field and its options are narrowed to the current tier, so leaving the
   * field behind left it holding an id absent from its own list — and the order would be submitted
   * naming a tier the buyer has moved out of. The server re-resolves the tier regardless, so this
   * is about the form agreeing with the charge rather than about the charge itself.
   *
   * `repointPriceToTier` is idempotent, so this settles in one pass.
   */
  useEffect(() => {
    if (!selectedPrice || selectedPrice.id === selectedPriceId) return

    form.setFieldValue('selectedPriceId', selectedPrice.id)
  }, [selectedPrice, selectedPriceId, form])

  /**
   * Savings baseline: the dearest plan the buyer could have chosen AT THEIR TIER.
   *
   * Their own tier, not the card's global maximum — the 1-9 rate is not a price a fifty-seat
   * school was ever offered, so quoting a saving against it would invent a discount nobody
   * declined.
   */
  const theMostExpensivePrice =
    tierPrices.length > 0
      ? tierPrices.reduce((dearest, current) => (current.price > dearest.price ? current : dearest))
      : undefined

  const data = useMemo(() => {
    if (!selectedPrice || !theMostExpensivePrice) return null
    return {
      selectedPricePerSeat: selectedPrice.price,
      selectedPriceDuration: selectedPrice.duration,
      theMostExpensivePricePerSeat: theMostExpensivePrice.price,
      totalSeat: seats ?? 0,
      voucher: selectedVoucher,
    }
  }, [selectedPrice, theMostExpensivePrice, seats, selectedVoucher])

  const isPending = checkoutMutation.isPending

  if (!selectedPrice || !theMostExpensivePrice || !data) {
    return (
      <section className='srd:flex srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-y-4'>
        <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black srd:text-center')}>
          Tidak ada paket harga tersedia.
        </p>
      </section>
    )
  }

  return (
    <section className='srd:text-neutral-black srd:flex srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-y-4'>
      <section className='srd:flex srd:w-full srd:flex-wrap srd:items-start srd:justify-center srd:gap-8'>
        <section className='srd:flex srd:w-full srd:max-w-[550px] srd:flex-col srd:gap-y-4'>
          <CheckoutCard
            organizationName={organizationData.name}
            title='Premium'
            primaryLine={
              <>
                <AnimatedCurrency value={selectedPrice.price * selectedPrice.duration} /> per kursi
              </>
            }
            secondaryLine={
              <>
                <AnimatedCurrency value={selectedPrice.price} /> per kursi per bulan
              </>
            }
          >
            <form.Field
              name='selectedPriceId'
              listeners={{
                onChange: ({ value: nextPriceId }) => {
                  if (!selectedVoucher || seats === null) return

                  const nextPrice = getAllPrice.find((price) => price.id === nextPriceId)
                  if (!nextPrice) return

                  if (!isVoucherStillValid(selectedVoucher, nextPriceId, nextPrice, seats)) {
                    form.setFieldValue('selectedVoucherId', null)
                  }
                },
              }}
            >
              {(field) => (
                <section className='srd:flex srd:flex-col srd:gap-y-2'>
                  <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>Paket</p>

                  <PriceCardGroup
                    disabled={isPending}
                    getAllPrice={tierPrices}
                    value={field.state.value}
                    onValueChange={(price) => field.handleChange(price.id)}
                  />

                  {termUpsell && (
                    <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
                      {/* Only the offer is the target, same as the seat nudge below the total. */}
                      <button
                        type='button'
                        disabled={isPending}
                        className='srd:text-blue-100 srd:underline srd:disabled:opacity-50'
                        onClick={() => field.handleChange(termUpsell.id)}
                      >
                        Pilih paket {termUpsell.duration} bulan
                      </button>{' '}
                      untuk lebih hemat, {formatCurrency(termUpsell.price)} per kursi per bulan
                    </p>
                  )}
                </section>
              )}
            </form.Field>

            <form.Field
              name='assignment'
              listeners={{
                onChange: ({ value: nextAssignment }) => {
                  if (!selectedVoucher || !selectedPrice || !nextAssignment) return

                  const nextSeats = countSeats(nextAssignment)

                  if (!isVoucherStillValid(selectedVoucher, selectedPrice.id, selectedPrice, nextSeats)) {
                    form.setFieldValue('selectedVoucherId', null)
                  }
                },
              }}
            >
              {(field) => (
                <>
                  <section className='srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-x-4 srd:gap-y-2'>
                    <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
                      Total kursi
                    </p>

                    {/* The count is a figure, not a control. Only "Ubah" opens the dialog, so the
                        hit target says what it does instead of relying on a pencil glyph beside a
                        number that looked editable in place. */}
                    <div className='srd:flex srd:items-center srd:gap-x-2'>
                      {seats === null ? (
                        <Skeleton className='srd:h-6 srd:w-8' />
                      ) : (
                        <NumberFlow
                          value={seats}
                          className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-black')}
                        />
                      )}

                      <Button
                        size='sm'
                        disabled={isPending || seats === null}
                        onClick={() => setAssignDialogOpen(true)}
                      >
                        Ubah
                      </Button>
                    </div>
                  </section>

                  {seats !== null && seats < 1 && (
                    <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-error srd:text-left')}>
                      Minimal 1 kursi untuk melanjutkan.
                    </p>
                  )}

                  <AssignSeatDialog
                    open={assignDialogOpen}
                    onOpenChange={setAssignDialogOpen}
                    type={type}
                    organizationId={organizationData.id}
                    value={field.state.value}
                    onConfirm={(value: SeatAssignmentValue) => {
                      assignmentSettledRef.current = true
                      field.handleChange(value)
                    }}
                  />
                </>
              )}
            </form.Field>

            <CheckoutReveal show={calculateVoucherDiscount(data) > 0}>
              <section className='srd:border-outline-variant srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-x-4 srd:gap-y-2 srd:border-t srd:pt-2'>
                <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
                  Diskon promo
                </p>

                <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
                  (
                  <AnimatedCurrency
                    value={calculateVoucherDiscount(data)}
                    className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}
                  />
                  )
                </p>
              </section>
            </CheckoutReveal>

            <section className='srd:border-outline-variant srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-x-4 srd:gap-y-2 srd:border-t srd:pt-2'>
              <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>Total</p>

              {seats === null ? (
                <Skeleton className='srd:h-7 srd:w-32' />
              ) : (
                <AnimatedCurrency
                  value={calculateAmountPaid(data)}
                  className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-black')}
                />
              )}
            </section>

            <section className='srd:mt-4 srd:flex srd:flex-col srd:gap-y-2'>
              {calculateSavings(data) > 0 && selectedPrice && (
                <SavingsLine
                  label='Hemat paket'
                  amount={calculateSavings(data)}
                  explanation={SAVINGS_EXPLANATION.term}
                />
              )}

              {tierSavings > 0 && (
                <SavingsLine label='Hemat volume' amount={tierSavings} explanation={SAVINGS_EXPLANATION.tier} />
              )}

              {/* The next tier is invisible from inside the current one — the card quotes one rate
                  and nothing says more seats cost less each. */}
              {upsell && (
                <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100 srd:text-right')}>
                  {/* Only the offer itself is the target — the rate after it is the reason, not an
                      action, and a whole sentence underlined reads as a link to somewhere. */}
                  <button
                    type='button'
                    disabled={isPending}
                    className='srd:text-blue-100 srd:underline srd:disabled:opacity-50'
                    onClick={handleTakeUpsell}
                  >
                    Tambah {upsell.seatsNeeded} kursi lagi
                  </button>{' '}
                  untuk lebih hemat, {formatCurrency(upsell.pricePerSeat)} per kursi per bulan
                </p>
              )}
            </section>

            {checkoutMutation.error && (
              <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-error srd:text-left')}>
                {checkoutMutation.error.message}
              </p>
            )}
          </CheckoutCard>
        </section>

        <section className='srd:flex srd:w-full srd:max-w-[510px] srd:flex-col srd:gap-y-4'>
          <form.Field name='selectedVoucherId'>
            {(field) => (
              <VoucherList
                className='srd:w-full'
                priceBeforeVoucher={calculateTotalPriceWithoutVoucher({ type: 'new', data })}
                onSelectVoucher={(voucher) => field.handleChange(voucher?.id ?? null)}
                selectedVoucherId={field.state.value}
                selectedPriceId={selectedPrice.id}
                type={type}
                disabled={isPending}
              />
            )}
          </form.Field>

          <section className='srd:border-outline-variant/50 srd:flex srd:w-full srd:flex-col srd:items-center srd:gap-y-2 srd:rounded-xl srd:border srd:p-4'>
            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-center srd:text-neutral-black')}>
              Merencanakan untuk banyak murid atau rombongan belajar? Kami dapat membantumu dapatkan nilai terbaik
              untukmu.
            </p>
            <Button asChild variant='ghost' size='sm' className='srd:text-neutral-black'>
              <a href={WHATSAPP_LINK} target='_blank' rel='noreferrer'>
                <WhatsappIcon className='srd:mr-2 srd:size-4 srd:shrink-0' />
                Mari berdiskusi
              </a>
            </Button>
          </section>
        </section>
      </section>

      <div className='srd:bg-neutral-white srd:fixed srd:bottom-0 srd:left-0 srd:h-fit srd:w-full srd:shadow-inner srd:z-50'>
        <div className='srd:mx-auto srd:flex srd:w-full srd:max-w-[1440px] srd:flex-wrap srd:items-center srd:justify-between srd:gap-x-6 srd:gap-y-2 srd:px-2 srd:py-4'>
          <section className='srd:flex srd:flex-col srd:gap-y-0'>
            <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>Total</p>

            {seats === null ? (
              <Skeleton className='srd:h-7 srd:w-32' />
            ) : (
              <AnimatedCurrency
                value={calculateAmountPaid(data)}
                className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-black')}
              />
            )}

            {calculateTotalDiscount(data, tierSavings) > 0 && (
              <p
                className={cn(
                  typographyVariants({ variant: 'body-md' }),
                  'srd:text-purple-100 srd:flex srd:items-center srd:gap-x-1',
                )}
              >
                Total hemat
                <AnimatedCurrency
                  value={calculateTotalDiscount(data, tierSavings)}
                  className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-purple-100')}
                />
              </p>
            )}
          </section>

          <Button
            variant='gradient'
            size='lg'
            loading={isPending}
            // Disabled, not hidden, while the count is still resolving: it comes back the moment the
            // default settles, and a failed roster read settles it too (see the effect above).
            disabled={isPending || seats === null || seats < 1}
            onClick={() => void form.handleSubmit()}
          >
            Konfirmasi
          </Button>
        </div>
      </div>
    </section>
  )
}
